import { Location } from '@angular/common';
import { Injectable, effect, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IRequestOptions } from '@core/interfaces/api-request-options.interface';
import { ApiService } from '@core/services/api.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CategoriesView } from '@shared/interfaces/api.interface';
import { IKeyValue } from '@shared/interfaces/key-value.interface';
import {
  Catalog,
  FilterProperty,
  FilterPropertyValue,
  FilterStatus,
  GetAllFilterPropertiesDto,
  IAuctionFilterDto,
} from '@shared/interfaces/search.interface';
import { Observable, Subject, map, throwError } from 'rxjs';
import {
  ArticleProperty,
  AuctionView,
  AuctionViewMinimal,
} from 'src/app/articles-manager/interfaces/auction.interface';
import { environment } from 'src/environments/environment';
import { LanguageService } from './language.service';

const isDeepStrictEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SearchService {
  auctionTypeList: IKeyValue[] = [];
  sportList: IKeyValue[] = [];
  competitionList: IKeyValue[] = [];
  teamList: IKeyValue[] = [];
  seasonList: IKeyValue[] = [];
  filtersChange = new Subject<IAuctionFilterDto>();
  /** Requests coming from parent components to perform a search in the filter component */
  private readonly _searchRequests = new Subject<{
    limit: number | null;
    append: boolean;
  }>();
  lastResult: AuctionView[] = [];
  lastSearch: Date;

  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly _api = inject(ApiService);
  private readonly location = inject(Location);

  private readonly allRoutes = ['tags', 'promoter', 'campaign'];

  lastResultByType: {
    [key: string]: {
      [key: number]: {
        lastResult: AuctionView[];
        lastSearch: Date;
        lastFilters: IAuctionFilterDto;
      };
    };
  } = {};
  filters: IAuctionFilterDto = {
    idLanguage: -1,
    searchText: '',
    idAuctionType: -1,
    idDetail: null,
    onlySigned: false,
    idSport: null,
    idCompetition: null,
    idTeam: null,
    idSeason: null,
    onlyNew: false,
    onlyExpiring: false,
    onlyArriving: false,
    onlyBuyNow: false,
    auctionStatus: FilterStatus.All,
    idEntityList: [],
    idCampaignList: [],
    idPromoterList: [],
    idActiveAuction: null,
    idTagList: [],
    catalog: Catalog.All,
    orderBy: 'evidence',
    orderDirection: 'desc',
  };
  searchRoute: string = '';

  private mapImageAuctionView = (auctions: AuctionViewMinimal[]) =>
    auctions.map(
      (a) =>
        ({
          ...a,
          articlePhoto: {
            fullPath: environment.ImageUrl + a.imagePath,
          } as any,
        } satisfies Partial<AuctionView>)
    ) as AuctionView[];

  constructor() {
    this.route.queryParams.subscribe((params) => {
      this.filters.searchText = params['searchText'] || '';
      this.filters.idAuctionType = params['idAuctionType']
        ? +params['idAuctionType']
        : -1;
      this.filters.idSport = params['idSport'] ? +params['idSport'] : -1;
      this.filters.idCompetition = params['idCompetition']
        ? +params['idCompetition']
        : -1;
      this.filters.idTeam = params['idTeam'] ? +params['idTeam'] : -1;
      this.filters.idSeason = params['idSeason'] ? +params['idSeason'] : -1;
      this.filters.onlyNew = params['onlyNew'] === 'true';
      this.filters.onlyExpiring = params['onlyExpiring'] === 'true';
      this.filters.onlyArriving = params['onlyArriving'] === 'true';
      this.filters.onlyFavorites = params['onlyFavorites'] === 'true';
      this.filters.onlySigned = params['onlySigned'] === 'true';
      this.filters.onlyBuyNow = params['onlyBuyNow'] === 'true';
      this.filters.idCompetition = params['idCompetition']
        ? +params['idCompetition']
        : -1;
      this.filters.idSeason =
        params['idSeason'] && this.filters.idSport > -1
          ? +params['idSeason']
          : -1;
      this.filters.idDetail = params['idDetail'] ? +params['idDetail'] : -1;
      this.filters.auctionStatus = params['auctionStatus']
        ? +params['auctionStatus']
        : this.allRoutes.some((route) => this.router.url.includes(`/${route}/`))
        ? FilterStatus.All
        : FilterStatus.OnlyActive;
      this.filters.orderBy = params['orderBy'] || 'evidence';
      this.filters.orderDirection = params['orderDirection'] || 'desc';

      this.filters.idLanguage =
        this.languageService.userSelectedLanguage?.idLanguage ?? -1;
    });

    effect(
      () => {
        const language = this.languageService.selectedLanguageSignal();
        if (
          language &&
          this.filters.idLanguage !== language.idLanguage &&
          this.filters.idLanguage !== -1
        ) {
          this.setFilters(this.searchRoute, {
            ...this.filters,
            idLanguage: language.idLanguage,
          });
        }
      }
    );
  }

  setFilters(nextRoute: string, filters: IAuctionFilterDto, force = false) {
    const filterChanged = force || !isDeepStrictEqual(this.filters, filters);

    if (filterChanged || this.searchRoute !== nextRoute) {
      this.filters = filters;
      this.searchRoute = nextRoute;
      this.filtersChange.next(filters);
      this.updateQueryParams(filters);
    }
  }

  listenFiltersChanges(): Observable<any> {
    return this.filtersChange.asObservable();
  }

  /** Parent components call this to request a search on the filter component */
  requestSearch(limit: number | null = null, append = false) {
    this._searchRequests.next({ limit, append });
  }

  listenSearchRequests(): Observable<{
    limit: number | null;
    append: boolean;
  }> {
    return this._searchRequests.asObservable();
  }

  getAllV1(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api.request<AuctionView[]>(
      ApiServiceHolder.auctionView.getAll(filters)
    );
  }

  getAllV2(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api
      .request<AuctionViewMinimal[]>(
        ApiServiceHolder.auctionView.getAllV2(filters)
      )
      .pipe(map(this.mapImageAuctionView));
  }

  getAllV2Cached(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api
      .requestCached<AuctionViewMinimal[]>(
        ApiServiceHolder.auctionView.getAllV2(filters)
      )
      .pipe(map(this.mapImageAuctionView));
  }

  getAllCached(filters: IAuctionFilterDto): Observable<AuctionView[]> {
    return this._api.requestCached<AuctionView[]>(
      ApiServiceHolder.auctionView.getAll(filters)
    );
  }

  getSuggested(
    idLanguage: number,
    idAuction: number
  ): Observable<{ memoraBid: AuctionView[]; marketPlace: AuctionView[] }> {
    return this._api.requestLocalStorageCached<{
      memoraBid: AuctionView[];
      marketPlace: AuctionView[];
    }>(
      ApiServiceHolder.auctionView.getSuggested(idLanguage, idAuction),
      60 * 60_000
    ); // cache for 1 hour
  }

  getAllAndCountV1(
    filters: IAuctionFilterDto
  ): Observable<{ count: number; auctions: AuctionView[] }> {
    return this._api.request<{ count: number; auctions: AuctionView[] }>(
      ApiServiceHolder.auctionView.getAllAndCountV2(filters)
    );
  }

  getAllAndCountCachedV1(
    filters: IAuctionFilterDto
  ): Observable<{ count: number; auctions: AuctionView[] }> {
    return this._api.requestCached<{ count: number; auctions: AuctionView[] }>(
      ApiServiceHolder.auctionView.getAllAndCountV1(filters)
    );
  }

  getAllAndCountV2(
    filters: IAuctionFilterDto
  ): Observable<{ count: number; auctions: AuctionView[] }> {
    return this._api
      .request<{ count: number; auctions: AuctionViewMinimal[] }>(
        ApiServiceHolder.auctionView.getAllAndCountV2(filters)
      )
      .pipe(
        map((a) => ({
          count: a.count,
          auctions: this.mapImageAuctionView(a.auctions),
        }))
      );
  }

  getAllAndCountCachedV2(
    filters: IAuctionFilterDto
  ): Observable<{ count: number; auctions: AuctionView[] }> {
    return this._api
      .requestCached<{ count: number; auctions: AuctionViewMinimal[] }>(
        ApiServiceHolder.auctionView.getAllAndCountV2(filters)
      )
      .pipe(
        map((a) => ({
          count: a.count,
          auctions: this.mapImageAuctionView(a.auctions),
        }))
      );
  }

  getAllForUser(
    idLanguage: number,
    userAuctionFilter: 'onlystarred' | 'onlywin' | 'onlylost'
  ): Observable<AuctionView[]> {
    let apiOptions: IRequestOptions;
    switch (userAuctionFilter) {
      case 'onlystarred':
        apiOptions = ApiServiceHolder.auction.getAllForUserStarred(idLanguage);
        break;
      case 'onlywin':
        apiOptions = ApiServiceHolder.auction.getAllForUserWin(idLanguage);
        break;
      case 'onlylost':
        apiOptions = ApiServiceHolder.auction.getAllForUserLost(idLanguage);
        break;
      default:
        throwError(() => 'No type selected.');
        break;
    }
    return this._api.request<AuctionView[]>(apiOptions);
  }

  getAllAuction(idLanguage: number): Observable<AuctionView[]> {
    return this._api.request(ApiServiceHolder.auction.getAll(idLanguage));
  }

  getAllTypes(): Observable<IKeyValue[]> {
    return this._api.requestLocalStorageCached<IKeyValue[]>(
      ApiServiceHolder.filter.getAllTypes()
    );
  }

  getAllFilterProperties(idArticleType: number): Observable<FilterProperty[]> {
    return this._api.request(
      ApiServiceHolder.filter.getAllFilterProperties(idArticleType)
    );
  }

  getFilterPropertyValue(
    itemOrder: number,
    value: string
  ): Observable<FilterPropertyValue[]> {
    return this._api.request(
      ApiServiceHolder.filter.getFilterPropertyValue(itemOrder, value)
    );
  }

  getAllProperties(
    filterProperties: GetAllFilterPropertiesDto
  ): Observable<ArticleProperty[]> {
    return this._api.request(
      ApiServiceHolder.filter.getAllProperties(filterProperties)
    );
  }

  getRelatedTagCategories(idTag: number, onlyActive: boolean = true) {
    return this._api.requestCached<Array<CategoriesView>>(
      ApiServiceHolder.categories.getRelatedTagCategories(idTag, onlyActive)
    );
  }
  getRelatedTagSellerCategories(
    idTag: number | null,
    idSeller: number | null,
    onlyActive: boolean = true
  ) {
    return this._api.requestCached<Array<CategoriesView>>(
      ApiServiceHolder.categories.getRelatedTagSellerCategories(
        idTag ?? -1,
        idSeller ?? -1,
        onlyActive
      )
    );
  }

  initializeRoute(url: string) {
    const filters: IAuctionFilterDto = {
      ...this.filters,
      searchText: '',
      idAuctionType: -1,
      idSport: -1,
      idCompetition: -1,
      idTeam: -1,
      onlyNew: false,
      onlyExpiring: false,
      onlyArriving: false,
      onlyFavorites: false,
      onlySigned: false,
      onlyBuyNow: false,
      idDetail: null,
      idTagList: [],
      idActiveAuction: null,
      idCampaignList: [],
      idCategoriesList: [],
      idEntityList: [],
      idPromoterList: [],
      orderBy: 'evidence',
      orderDirection: 'desc',
    };

    const params = url.split('/');
    const currentPageRoute = params[params.length - 1];
    this.searchRoute = currentPageRoute;
    switch (currentPageRoute) {
      case 'search-result-signed': {
        filters.onlySigned = true;
        this.filters = filters;
        break;
      }
      case 'search-result-store': {
        filters.idDetail = 1;
        this.filters = filters;
        break;
      }
      case 'search-result-worn': {
        filters.idDetail = 2;
        this.filters = filters;
        break;
      }
      case 'search-result-all': {
        this.filters = filters;
        break;
      }
      default: {
        if (this.filters.idSport == null) {
          this.filters.idSport = -1;
        }
        if (this.filters.idCompetition == null) {
          this.filters.idCompetition = -1;
        }
        if (this.filters.idTeam == null) {
          this.filters.idTeam = -1;
        }
        if (this.filters.idSeason == null) {
          this.filters.idSeason = -1;
        }
        break;
      }
    }
  }

  updateQueryParams(filter: IAuctionFilterDto) {
    const defaultAuctionStatus = this.allRoutes.some((route) =>
      this.router.url.includes(`/${route}/`)
    )
      ? FilterStatus.All
      : FilterStatus.OnlyActive;
    const auctionStatus =
      filter.auctionStatus === defaultAuctionStatus
        ? null
        : filter.auctionStatus;

    const queryParams: Partial<IAuctionFilterDto> = {
      searchText: filter.searchText ? filter.searchText : null,
      idAuctionType: filter.idAuctionType === -1 ? null : filter.idAuctionType,
      idSport: filter.idSport === -1 ? null : filter.idSport,
      idCompetition: filter.idCompetition === -1 ? null : filter.idCompetition,
      idTeam: filter.idTeam === -1 ? null : filter.idTeam,
      idSeason: filter.idSeason === -1 ? null : filter.idSeason,
      onlyNew: filter.onlyNew ? filter.onlyNew : null,
      onlyExpiring: filter.onlyExpiring ? filter.onlyExpiring : null,
      onlyArriving: filter.onlyArriving ? filter.onlyArriving : null,
      onlyFavorites: filter.onlyFavorites ? filter.onlyFavorites : null,
      onlySigned: filter.onlySigned ? filter.onlySigned : null,
      onlyBuyNow: filter.onlyBuyNow ? filter.onlyBuyNow : null,
      idDetail: filter.idDetail === -1 ? null : filter.idDetail,
      orderBy: filter.orderBy === 'evidence' ? null : filter.orderBy,
      orderDirection:
        filter.orderDirection === 'desc' ? null : filter.orderDirection,
      auctionStatus: auctionStatus,
    };

    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });

    // Keep the router's history state (its `navigationId`) on the entry:
    // `replaceState` defaults it to null, which erases the marker the router
    // needs to restore the scroll position when the user navigates back.
    this.location.replaceState(
      this.router.serializeUrl(urlTree),
      '',
      this.location.getState()
    );
  }
}
